/**
 * Travel-Hub — Countdown + Twingle-Spenden-Progress.
 *
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *  STATUS (Stand 2026-05-19): Twingle-Teil PAUSIERT — wartet auf Proxy.
 *  Countdown läuft normal. Twingle-Block wird beim Init aus dem DOM entfernt.
 *  Wiederanschalten: `initTwingle(section)` in `init()` unten zurückbauen
 *  und `fetchTwingleReport()` auf den serverseitigen Proxy zeigen lassen.
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * Erwartetes Markup:
 *   <section data-travelhub
 *            data-reise-start="2026-09-04"
 *            data-twingle-project="wsz-website"
 *            data-spendenziel="50000">
 *     <div data-countdown>
 *       <span data-cd="days">--</span>
 *       <span data-cd="hours">--</span>
 *       <span data-cd="minutes">--</span>
 *       <span data-cd="seconds">--</span>
 *     </div>
 *     <div data-twingle-block>
 *       <span data-twingle-current>– €</span>
 *       <span data-twingle-percent>0 %</span>
 *       <span data-twingle-donors>– Unterstützer:innen</span>
 *       <div data-twingle-progress-fill style="width:0%"></div>
 *       <div data-twingle-progressbar></div>
 *       <p data-twingle-empty hidden></p>
 *     </div>
 *   </section>
 */

const CACHE_TTL_MS = 5 * 60 * 1000;       // 5 Minuten — schont API + spürt Updates schnell
const STORAGE_PREFIX = "wsz_twingle_";

const EUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat("de-DE");

// ──────────────────────────── Countdown ────────────────────────────

function pad(n) {
  return String(n).padStart(2, "0");
}

const FLIP_DURATION_MS = 600;

function createDigitElement() {
  const digit = document.createElement("span");
  digit.className = "flip-digit";
  digit.dataset.current = "";
  digit.innerHTML =
    '<span class="flip-digit__top"><span>0</span></span>' +
    '<span class="flip-digit__bottom"><span>0</span></span>' +
    '<span class="flip-digit__top-flap" aria-hidden="true"><span>0</span></span>' +
    '<span class="flip-digit__bottom-flap" aria-hidden="true"><span>0</span></span>';
  return digit;
}

function ensureDigitElements(container, count) {
  while (container.children.length < count) {
    container.appendChild(createDigitElement());
  }
  while (container.children.length > count) {
    container.removeChild(container.lastChild);
  }
}

function setDigitInstant(digit, ch) {
  digit.querySelector(".flip-digit__top > span").textContent = ch;
  digit.querySelector(".flip-digit__bottom > span").textContent = ch;
  digit.dataset.current = ch;
}

function flipDigitTo(digit, oldCh, newCh) {
  const topSpan = digit.querySelector(".flip-digit__top > span");
  const bottomSpan = digit.querySelector(".flip-digit__bottom > span");
  const topFlapSpan = digit.querySelector(".flip-digit__top-flap > span");
  const bottomFlapSpan = digit.querySelector(".flip-digit__bottom-flap > span");

  // Static-Top zeigt sofort den NEW-Char (dahinter, wird sichtbar, sobald top-flap wegklappt).
  topSpan.textContent = newCh;
  // Static-Bottom bleibt OLD, bis die Animation in der zweiten Hälfte den bottom-flap mit NEW deckt.
  bottomSpan.textContent = oldCh;
  // Top-Flap zeigt OLD und klappt nach vorn unten weg.
  topFlapSpan.textContent = oldCh;
  // Bottom-Flap zeigt NEW und klappt von oben in die untere Hälfte herein.
  bottomFlapSpan.textContent = newCh;

  // Animation neu triggern: Klasse entfernen, Reflow erzwingen, dann wieder setzen.
  digit.classList.remove("is-flipping");
  // eslint-disable-next-line no-unused-expressions
  digit.offsetWidth;
  digit.classList.add("is-flipping");

  digit.dataset.current = newCh;

  // Static-Bottom am Ende auf NEW setzen und Klasse aufräumen.
  window.setTimeout(() => {
    bottomSpan.textContent = newCh;
    digit.classList.remove("is-flipping");
  }, FLIP_DURATION_MS);
}

function updateFlipContainer(container, value, padLen) {
  const str = String(value).padStart(padLen, "0");
  ensureDigitElements(container, str.length);
  Array.from(container.children).forEach((digit, i) => {
    const newCh = str[i];
    const oldCh = digit.dataset.current;
    if (oldCh === "") {
      setDigitInstant(digit, newCh);
    } else if (oldCh !== newCh) {
      flipDigitTo(digit, oldCh, newCh);
    }
  });
}

function tickCountdown(target, container) {
  const now = Date.now();
  const remaining = target - now;
  const cells = {
    days: container.querySelector('[data-cd="days"]'),
    hours: container.querySelector('[data-cd="hours"]'),
    minutes: container.querySelector('[data-cd="minutes"]'),
    seconds: container.querySelector('[data-cd="seconds"]'),
  };

  const padFor = (el, fallback) =>
    el && el.dataset.pad ? parseInt(el.dataset.pad, 10) : fallback;

  if (remaining <= 0) {
    if (cells.days) updateFlipContainer(cells.days, 0, padFor(cells.days, 3));
    if (cells.hours) updateFlipContainer(cells.hours, 0, 2);
    if (cells.minutes) updateFlipContainer(cells.minutes, 0, 2);
    if (cells.seconds) updateFlipContainer(cells.seconds, 0, 2);
    return false;
  }

  const seconds = Math.floor(remaining / 1000) % 60;
  const minutes = Math.floor(remaining / (1000 * 60)) % 60;
  const hours = Math.floor(remaining / (1000 * 60 * 60)) % 24;
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));

  if (cells.days) updateFlipContainer(cells.days, days, padFor(cells.days, 3));
  if (cells.hours) updateFlipContainer(cells.hours, hours, 2);
  if (cells.minutes) updateFlipContainer(cells.minutes, minutes, 2);
  if (cells.seconds) updateFlipContainer(cells.seconds, seconds, 2);
  return true;
}

function initCountdown(section) {
  const container = section.querySelector("[data-countdown]");
  if (!container) return;

  const dateStr = section.dataset.reiseStart;
  if (!dateStr) return;

  // 04.09.2026 00:00 lokal — den Startpunkt früh am Tag legen
  const target = new Date(dateStr + "T00:00:00").getTime();
  if (!Number.isFinite(target)) return;

  if (!tickCountdown(target, container)) return;
  const handle = window.setInterval(() => {
    if (!tickCountdown(target, container)) {
      window.clearInterval(handle);
    }
  }, 1000);
}

// ──────────────────────────── Twingle ────────────────────────────

function loadCache(projectId) {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + projectId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function saveCache(projectId, data) {
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + projectId,
      JSON.stringify({ ts: Date.now(), data }),
    );
  } catch (_) {
    /* private mode / quota — silent */
  }
}

async function fetchTwingleReport(projectId) {
  // Twingle Spenden-Reporting-Endpoint — ersetzt durch echten Endpunkt, falls anders dokumentiert.
  const url = `https://spendenformular.twingle.de/api/${encodeURIComponent(projectId)}/report`;
  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "omit",
    mode: "cors",
  });
  if (!res.ok) throw new Error("twingle http " + res.status);
  return res.json();
}

/**
 * Normalisiert verschiedene Twingle-Response-Schemas auf {current, donors}.
 * Twingle hat mehrere historische Felder; wir tasten uns durch.
 */
function normaliseTwingleData(raw) {
  if (!raw || typeof raw !== "object") return null;
  const current =
    Number(raw.current) ||
    Number(raw.amount) ||
    Number(raw.donated) ||
    Number(raw.total) ||
    Number(raw.sum) ||
    null;
  const donors =
    Number(raw.donors) ||
    Number(raw.donor_count) ||
    Number(raw.donations) ||
    Number(raw.count) ||
    null;
  if (current === null) return null;
  return { current, donors };
}

function renderTwingleData(section, data) {
  const target = Number(section.dataset.spendenziel) || 0;
  const block = section.querySelector("[data-twingle-block]");
  if (!block) return;

  const current = data ? data.current : null;
  const donors = data ? data.donors : null;

  const elCurrent = block.querySelector("[data-twingle-current]");
  const elPercent = block.querySelector("[data-twingle-percent]");
  const elDonors = block.querySelector("[data-twingle-donors]");
  const elFill = block.querySelector("[data-twingle-progress-fill]");
  const elBar = block.querySelector("[data-twingle-progressbar]");
  const elEmpty = block.querySelector("[data-twingle-empty]");

  if (current === null) {
    if (elEmpty) elEmpty.hidden = false;
    return;
  }
  if (elEmpty) elEmpty.hidden = true;

  const percent = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;

  if (elCurrent) elCurrent.textContent = EUR.format(current);
  if (elPercent) elPercent.textContent = NUM.format(percent) + " %";
  if (elFill) elFill.style.width = percent + "%";
  if (elBar) elBar.setAttribute("aria-valuenow", String(percent));

  if (elDonors) {
    if (donors !== null && donors > 0) {
      elDonors.textContent = `${NUM.format(donors)} Unterstützer:innen`;
    } else {
      elDonors.textContent = "Werde Teil der Spender:innen-Liste";
    }
  }
}

async function initTwingle(section) {
  const block = section.querySelector("[data-twingle-block]");
  if (!block) return;

  const projectId = section.dataset.twingleProject;
  if (!projectId) return;

  // Sofort aus Cache rendern, falls vorhanden — keine Wartezeit
  const cached = loadCache(projectId);
  if (cached) renderTwingleData(section, cached);

  // Frischen Stand holen, fail-soft
  try {
    const raw = await fetchTwingleReport(projectId);
    const data = normaliseTwingleData(raw);
    if (data) {
      saveCache(projectId, data);
      renderTwingleData(section, data);
    } else if (!cached) {
      renderTwingleData(section, null);
    }
  } catch (err) {
    if (!cached) renderTwingleData(section, null);
    console.warn("travel-hub: twingle fetch failed", err);
  }
}

// ──────────────────────────── Init ────────────────────────────

export function init() {
  const sections = document.querySelectorAll("[data-travelhub]");
  sections.forEach((section) => {
    initCountdown(section);
    // TODO Twingle-Proxy: Sobald ein serverseitiger Endpoint mit API-Key steht,
    // `initTwingle(section)` hier wieder aufrufen und den block.remove()
    // unten löschen.
    const block = section.querySelector("[data-twingle-block]");
    if (block) block.remove();
  });
}
